/**
 * Call cards: "Who rang?" answered as one card per call the receptionist took
 * (docs/intelligent-chat-answers-plan.md, "Call cards", wave 3).
 *
 * Built by the SERVER from `receptionist_calls` rows, never by the model. The
 * read lives in `./call-cards-data.ts`; this file is the pure half, so the
 * route, the panel, the Mucka Bar and the oversight grader can all import it
 * (anti-pattern #18).
 *
 * 🚨 The tag on a card is DERIVED from `receptionist_calls.status` through the
 * Calls inbox's own `callPill`, so the chat and the inbox can never call the
 * same call two different things. The one status `callStatusPill` does not
 * know (`reschedule_staged`, which the CHECK has allowed since 2026-09-14) is
 * named here; anything else it does not know is called a plain "Call" rather
 * than shown as a raw database word.
 *
 * 🚨 Nothing stored here expires. A recording is played from the gated route
 * `/api/voice-receptionist/calls/<id>/recording`, which mints a five-minute
 * signed URL per play, so a reply redrawn next month asks the route again and
 * the route decides. The card keeps the expiry date only so play can hide
 * itself once the recording has been deleted.
 */
import {
  callBlurb,
  callPill,
  formatCallDate,
  formatDuration,
} from '@/components/voice-receptionist/calls/call-labels';
import type { StatusVariant } from '@/components/ui/StatusPill';
import { formatUkNumber } from '@/lib/phone/format-uk';
import type { StagedBooking } from '@/lib/voice-receptionist/staged-booking';

/** Cards shown; the rest are counted, never dropped silently. */
export const CALL_CARDS_MAX = 5;

/** How far back "Who rang?" looks when the caller does not say. */
export const CALL_CARDS_DEFAULT_DAYS = 7;

/** The Calls inbox, where "Open all calls" goes. */
export const CALLS_PAGE_PATH = '/voice-receptionist';

/** A tag's colour. Orange is never one: a tag is not a tap. */
export type CallTagTone = 'amber' | 'green' | 'red' | 'blue' | 'dim';

export interface CallCardRecording {
  /** "2m 14s", or null when the call row has no duration. */
  length: string | null;
  /** ISO. Play hides once this has passed. Null when no expiry was stamped. */
  expiresAt: string | null;
}

export interface CallCardNext {
  label: string;
  path: string;
}

export interface CallCard {
  /** `receptionist_calls.id`, which is also what the recording route takes. */
  callId: string;
  /** The client on file, else the number they can be rung back on. */
  who: string;
  tag: string;
  tone: CallTagTone;
  /** "Today, 11:42am", London, built server-side. */
  time: string;
  /** The call's own summary, or the inbox's honest line when it has none. */
  summary: string;
  /** Null when there is no recording, it has expired, or the seat may not play it. */
  recording: CallCardRecording | null;
  /** What to do about it: the booking it pencilled, or the client. */
  next: CallCardNext | null;
  /** The call in the inbox. */
  path: string;
}

export interface CallCardsBlock {
  kind: 'call-cards';
  cards: CallCard[];
  /** Every inbound call in the window, counted by Postgres. */
  total: number;
  /** Calls in the window not shown as cards. */
  more: number;
  /** "in the last 7 days", so the sentence and the block say the same window. */
  window: string;
  /** Where "Open all calls" goes. */
  allPath: string;
}

/** One call as `call-cards-data.ts` read it, reduced to what a card needs. */
export interface CallCandidate {
  id: string;
  status: string;
  direction: string;
  outcome: string | null;
  startedAt: string | null;
  durationSeconds: number | null;
  summary: string | null;
  /** True when `recording_url` holds a path. The path itself never leaves the server. */
  hasRecording: boolean;
  recordingExpiresAt: string | null;
  clientId: string | null;
  clientName: string | null;
  jobId: string | null;
  /** What became of a staged booking (`loadStagedBookings`); absent reads as still waiting. */
  booking?: StagedBooking | null;
  /** E.164 the caller can be rung back on (`callbackNumber`), or null. */
  callbackE164: string | null;
}

/** What the seat may open or play. Worked out where the block is built. */
export interface CallCardsAccess {
  /** `manage_voice_receptionist`: the same key the recording route checks. */
  playRecordings: boolean;
  /** `/jobs` opens for this seat (`pathOpensFor`). */
  openJobs: boolean;
  /** `/clients` opens for this seat. */
  openClients: boolean;
}

const VARIANT_TONE: Record<StatusVariant, CallTagTone> = {
  pending: 'amber',
  completed: 'green',
  cancelled: 'red',
  scheduled: 'blue',
  'on-site': 'blue',
  travelling: 'blue',
  'quote-sent': 'blue',
};

/** The tag for a call, from its status. Never written by the model. */
export function callTag(
  c: Pick<CallCandidate, 'status' | 'startedAt' | 'direction' | 'outcome' | 'booking'> & { durationSeconds?: number | null },
  now: number,
): { tag: string; tone: CallTagTone } {
  if (c.status === 'reschedule_staged') return { tag: 'Change to approve', tone: 'amber' };
  const pill = callPill({ status: c.status, startedAt: c.startedAt, direction: c.direction, outcome: c.outcome, booking: c.booking ?? null, durationSeconds: c.durationSeconds }, now);
  // `callStatusPill` hands an unknown status back as its own label. A raw
  // database word is not something to show a plumber.
  if (pill.label === c.status) return { tag: 'Call', tone: 'dim' };
  return { tag: pill.label, tone: VARIANT_TONE[pill.variant] ?? 'dim' };
}

/**
 * `+447700900123` → `07700 900123`, and any other 11-digit UK number the same
 * way (`formatUkNumber`); a shorter UK number gets its leading 0 back
 * unspaced, since those share no grouping. Anything else is shown as it came.
 */
export function ukNumber(e164: string): string {
  const mobile = formatUkNumber(e164);
  if (mobile !== e164) return mobile;
  return /^\+44\d{9,10}$/.test(e164) ? `0${e164.slice(3)}` : e164;
}

/** Whether a recording can still be played at `now`. */
export function recordingPlayable(recording: CallCardRecording | null, now: number): boolean {
  if (!recording) return false;
  if (!recording.expiresAt) return true;
  const at = Date.parse(recording.expiresAt);
  return !Number.isFinite(at) || at > now;
}

/** The route the `<audio>` element plays from. Gated, account-scoped, signed per play. */
export function recordingPath(callId: string): string {
  return `/api/voice-receptionist/calls/${encodeURIComponent(callId)}/recording`;
}

function windowLabel(days: number): string {
  return days === 1 ? 'in the last day' : `in the last ${days} days`;
}

function nextStep(c: CallCandidate, access: CallCardsAccess): CallCardNext | null {
  const staged = c.status === 'booking_staged' || c.status === 'reschedule_staged';
  if (staged && c.jobId && access.openJobs) {
    return { label: 'Open the booking', path: `/jobs?open=${encodeURIComponent(c.jobId)}` };
  }
  if (c.clientId && access.openClients) {
    return { label: c.clientName ? `Open ${c.clientName}` : 'Open the client', path: `/clients/${encodeURIComponent(c.clientId)}` };
  }
  return null;
}

/**
 * The cards, or null when there is nothing to show. `candidates` are the
 * newest calls in the window, newest first; `total` is the count of every call
 * in it, from Postgres, never `candidates.length` (anti-pattern #27's cousin:
 * a count of loaded rows reads as a fact and is wrong on every busy account).
 */
export function buildCallCardsBlock(
  candidates: readonly CallCandidate[],
  total: number,
  access: CallCardsAccess,
  opts: { now: number; days?: number },
): CallCardsBlock | null {
  if (candidates.length === 0) return null;
  const { now } = opts;
  const days = opts.days ?? CALL_CARDS_DEFAULT_DAYS;
  const cards: CallCard[] = candidates.slice(0, CALL_CARDS_MAX).map(c => {
    const { tag, tone } = callTag(c, now);
    const recording: CallCardRecording | null = access.playRecordings && c.hasRecording
      ? { length: c.durationSeconds != null && c.durationSeconds > 0 ? formatDuration(c.durationSeconds) : null, expiresAt: c.recordingExpiresAt }
      : null;
    return {
      callId: c.id,
      who: c.clientName?.trim() || (c.callbackE164 ? ukNumber(c.callbackE164) : 'No number came through'),
      tag,
      tone,
      time: formatCallDate(c.startedAt, now),
      summary: callBlurb({ status: c.status, startedAt: c.startedAt, summary: c.summary, durationSeconds: c.durationSeconds }, now),
      recording: recordingPlayable(recording, now) ? recording : null,
      next: nextStep(c, access),
      path: `${CALLS_PAGE_PATH}?call=${encodeURIComponent(c.id)}`,
    };
  });
  const counted = Math.max(total, candidates.length);
  return {
    kind: 'call-cards',
    cards,
    total: counted,
    more: Math.max(0, counted - cards.length),
    window: windowLabel(days),
    allPath: CALLS_PAGE_PATH,
  };
}

/**
 * The calls, for the model, appended to the tool's own result. It needs them:
 * receptionist calls are not in `memory_episodes`, so without this the reply
 * to "Who rang?" would have nothing to say and the block would be the only
 * place the answer lived, which is the one thing a block may never be.
 *
 * Says what it looked at (inbound calls, the window), never that nothing else
 * exists (#31).
 */
export function callCardsResultText(block: CallCardsBlock): string {
  const lines = block.cards.map(c => `- ${c.time} · ${c.who} · ${c.tag} · ${c.summary.slice(0, 240)}`);
  const more = block.more ? `\n…and ${block.more} more ${block.window}, on the Calls page.` : '';
  return `Calls the AI receptionist took ${block.window}, newest first (${block.total} in all):\n${lines.join('\n')}${more}\n`
    + `These calls are on screen as call cards, each with its summary${block.cards.some(c => c.recording) ? ' and a play button for the recording' : ''}. `
    + 'Do not read every summary out; say who rang and name anything that needs the owner, such as a booking to approve or a message to return.';
}

/** When the window holds no calls. A statement about the window, not about the phone. */
export function noCallsResultText(days: number = CALL_CARDS_DEFAULT_DAYS): string {
  return `The AI receptionist has no calls on record ${windowLabel(days)}.`;
}

/** When the seat cannot open the Calls page. No rows, no count (#23). */
export const CALLS_NOT_FOR_THIS_SEAT =
  'Receptionist calls are not shown to this person: their role cannot open the receptionist\'s calls. '
  + 'If they ask who rang, say that is on the owner\'s side and someone with access can check. Do not say nobody rang.';

/** When the read failed. "Could not look" and "nothing there" are opposite answers. */
export const CALLS_UNREADABLE =
  'Could not read the receptionist\'s calls just now, so say you could not check them rather than that nobody rang.';

/**
 * One line for the oversight grader: counts and states only, nothing a caller
 * could be identified by.
 */
export function summariseCallCardsBlock(block: CallCardsBlock): string {
  const byTag = new Map<string, number>();
  for (const c of block.cards) byTag.set(c.tag, (byTag.get(c.tag) ?? 0) + 1);
  const tags = [...byTag.entries()].map(([t, n]) => `${n} ${t.toLowerCase()}`).join(', ');
  const playable = block.cards.filter(c => c.recording).length;
  return `[call-cards: ${block.total} call${block.total === 1 ? '' : 's'} ${block.window}, ${block.cards.length} shown`
    + `${tags ? ` (${tags})` : ''}, ${playable} with a recording${block.more ? `, ${block.more} more not shown` : ''}]`;
}
